import { useState } from 'react';
import { formatTypeTitle, segmentHasData, getSegmentMetrics, SEGMENTS } from './StatsHelpers';
import SummaryDoughnut from './SummaryDoughnut';
import MetricBar from './MetricBar';

const SingleStatsChart = ({ stats, responseSummary }) => {
  const { summary, type } = stats;
  const title = formatTypeTitle(type);

  const availableSegments = SEGMENTS.filter(seg => segmentHasData(stats[seg.key]));

  const [activeSegment, setActiveSegment] = useState(() => {
    // Pick the first segment that has a non-empty filtered array
    const filtered = availableSegments.find(s => {
      const arr = stats[s.filteredKey];
      return arr && arr.length > 0;
    });
    if (filtered) return filtered.key;
    // Fallback: departments first, then first available
    const dept = availableSegments.find(s => s.key === 'departments');
    return dept ? dept.key : availableSegments[0]?.key || null;
  });

  // Parse the responseSummary string into individual bullet lines
  const summaryLines = (() => {
    if (!responseSummary || typeof responseSummary !== 'string') return [];
    return responseSummary
      .split('\n')
      .map(line => line.replace(/^●\s*/, '').trim())
      .filter(Boolean);
  })();

  const activeSegmentDef = availableSegments.find(s => s.key === activeSegment);
  const segmentData = activeSegment ? stats[activeSegment] : null;
  const segmentMetrics = getSegmentMetrics(segmentData);

  // Get the filtered names for the active segment (e.g. ["Virginia"] for locations)
  const filteredNames = (() => {
    if (!activeSegmentDef) return new Set();
    const arr = stats[activeSegmentDef.filteredKey];
    return new Set(arr && arr.length > 0 ? arr : []);
  })();

  return (
    <div className="tw-bg-white tw-rounded-lg tw-p-3 tw-border tw-border-gray-200">
      <p className="tw-text-sm tw-font-semibold tw-text-gray-500 tw-uppercase tw-tracking-wide tw-m-0 tw-mb-2">{title}</p>

      {summaryLines.length > 0 && (
        <div className="tw-mb-3 tw-rounded-md tw-px-3 tw-py-2" style={{ backgroundColor: '#f5f0ff', borderLeft: '3px solid #830bd4' }}>
          <ul className="tw-list-none tw-m-0 tw-p-0 tw-space-y-1">
            {summaryLines.map((line, i) => (
              <li key={i} className="tw-text-xs tw-text-gray-700 tw-flex tw-items-start tw-gap-1.5">
                <span className="tw-mt-0.5 tw-shrink-0 tw-w-1.5 tw-h-1.5 tw-rounded-full tw-inline-block" style={{ backgroundColor: '#830bd4' }} />
                {line}
              </li>
            ))}
          </ul>
        </div>
      )}

      <SummaryDoughnut summary={summary} title={title} type={type} />
      {availableSegments.length > 0 && (
        <div className="tw-flex tw-items-end tw-mb-3 tw-border-0 tw-border-b tw-border-solid tw-border-gray-200">
          <div className="tw-flex tw-gap-6">
          {availableSegments.map(seg => {
            const Icon = seg.icon;
            const isActive = activeSegment === seg.key;
            const hasFilter = stats[seg.filteredKey] && stats[seg.filteredKey].length > 0;
            return (
              <button
                key={seg.key}
                onClick={() => setActiveSegment(seg.key)}
                className={`tw-flex tw-items-center tw-gap-1.5 tw-px-1 tw-pb-2 tw-bg-transparent tw-border-0 tw-border-b-2 tw-border-solid tw-text-sm tw-cursor-pointer tw-transition-colors ${
                  isActive
                    ? 'tw-font-semibold'
                    : 'tw-border-transparent tw-text-gray-400 hover:tw-text-gray-600'
                }`}
                style={isActive ? { color: '#830bd4', borderBottomColor: '#830bd4' } : {}}
              >
                <Icon className="tw-w-4 tw-h-4" />
                {seg.label}
                {hasFilter && (
                  <span
                    className="tw-w-2 tw-h-2 tw-rounded-full tw-inline-block tw-ml-0.5"
                    style={{ backgroundColor: '#830bd4' }}
                  />
                )}
              </button>
            );
          })}
          </div>
          <span className="tw-ml-auto tw-text-xs tw-text-gray-400 tw-italic tw-pb-2 tw-whitespace-nowrap">*Showing top &amp; bottom 5</span>
        </div>
      )}
      {segmentMetrics.map(({ metric, entries }) => (
        <MetricBar key={metric} metric={metric} entries={entries} filteredNames={filteredNames} />
      ))}
    </div>
  );
};

export default SingleStatsChart;
